import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { tagsApi } from "@/api/tags";
import type { TagRow } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

type EditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: TagRow };

/**
 * Tags settings page (#39). The user creates global tags here; tag
 * attachment to indexers / media happens on the per-entity detail
 * pages (TagPills component). The page is intentionally minimalist —
 * a tag is just (name, color), and the chip rendering lives in
 * TagChip so we can reuse it everywhere.
 */
export function Tags() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditingState>({ mode: "closed" });

  const { data: tags, isLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: tagsApi.list,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => tagsApi.remove(id),
    onSuccess: () => {
      toast("Tag removed", "success");
      // Bust everything that may render a tag — chips on indexer + media
      // pages will re-fetch their attached set automatically.
      qc.invalidateQueries({ queryKey: ["tags"] });
      qc.invalidateQueries({ queryKey: ["indexer-tags"] });
      qc.invalidateQueries({ queryKey: ["media-tags"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tags"
        description="Label indexers and media to filter searches by intersection."
        actions={<Button onClick={() => setEditing({ mode: "create" })}>Add tag</Button>}
      />

      {isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : !tags || tags.length === 0 ? (
        <EmptyState
          title="No tags yet"
          description="Create tags like 'private', '4k', or 'anime' to route searches to a subset of your indexers."
          action={<Button onClick={() => setEditing({ mode: "create" })}>Add tag</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Tag</th>
                <th className="px-4 py-2 font-medium">Color</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tags.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-4 py-2">
                    <TagChip tag={row} />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-foreground/60">
                    {row.color ?? "—"}
                  </td>
                  <td className="space-x-2 px-4 py-2 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditing({ mode: "edit", row })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (
                          confirm(
                            `Remove tag "${row.name}"? Detaches it from any indexer or media item that carries it.`,
                          )
                        ) {
                          removeMut.mutate(row.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TagFormModal editing={editing} onClose={() => setEditing({ mode: "closed" })} />
    </div>
  );
}

interface FormState {
  name: string;
  color: string;
}

const blankForm = (): FormState => ({ name: "", color: "" });
const formFromRow = (row: TagRow): FormState => ({
  name: row.name,
  color: row.color ?? "",
});

function TagFormModal({
  editing,
  onClose,
}: {
  editing: EditingState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";
  const editingRow = editing.mode === "edit" ? editing.row : null;

  useEffect(() => {
    if (editing.mode === "create") setForm(blankForm());
    if (editing.mode === "edit") setForm(formFromRow(editing.row));
  }, [editing]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit && editingRow) {
        await tagsApi.update(editingRow.id, {
          name: form.name,
          // Empty string clears the color back to NULL.
          color: form.color.trim() ? form.color.trim() : null,
        });
        toast("Tag updated", "success");
      } else {
        await tagsApi.create({
          name: form.name,
          color: form.color.trim() ? form.color.trim() : null,
        });
        toast("Tag created", "success");
      }
      qc.invalidateQueries({ queryKey: ["tags"] });
      qc.invalidateQueries({ queryKey: ["indexer-tags"] });
      qc.invalidateQueries({ queryKey: ["media-tags"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit tag" : "Add tag"}>
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Name">
          <TextInput
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="private"
            maxLength={64}
            required
          />
        </Field>
        <Field
          label="Color"
          hint="Optional. CSS color string (hex, named, etc.) used as the chip background."
        >
          <div className="flex items-center gap-2">
            <TextInput
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder="#7aa2f7"
              maxLength={32}
            />
            {form.color.trim() && (
              <span
                className="inline-block h-6 w-6 rounded border border-line"
                style={{ background: form.color }}
              />
            )}
          </div>
        </Field>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Tiny chip rendering primitive for a single tag. Exported so the
 * Indexers and per-media detail pages can render the same treatment
 * without importing the heavyweight TagPills picker.
 */
export function TagChip({ tag }: { tag: TagRow }) {
  const style = tag.color
    ? {
        background: tag.color,
        // Pick a contrast color blindly — the user is responsible for
        // colors they can read against the chip background. Keeping
        // text dark covers the common pastel-on-light-bg case.
        color: "#0b0f17",
      }
    : undefined;
  return (
    <span
      className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80"
      style={style}
    >
      {tag.name}
    </span>
  );
}
